import i18next from "i18next";
import api from "./api";
import { getApiErrorMessage } from "../utils/apiError";

interface LoginRequest {
  /** Email or portal login handle (backend accepts `email` or `identifier`) */
  email: string;
  password: string;
}

interface LoginResponse {
  access_token: string;
  token_type: string;
  requires_2fa: boolean;
  session_token: string | null;
  grace_period_started: boolean;
  grace_days_remaining: number | null;
}

// API response type (snake_case from backend)
// Story 6-6-6, 6-6-9: Added 2FA and grace period fields
interface ApiUser {
  id: string;
  email: string | null;
  pseudo_login?: string | null;
  full_name: string | null;
  role: string;
  created_at: string; // ISO datetime string
  is_active: boolean;
  locale?: string; // Story 11.1: i18n locale preference
  totp_enabled?: boolean;
  totp_disabled_by_admin?: boolean; // Story 6-6-9: show admin recovery banner
  totp_grace_started?: boolean;
  totp_grace_days_remaining?: number | null;
}

// Frontend User type (camelCase for consistency)
// Story 6-6-6: Added createdAt field
interface User {
  id: string;
  email: string | null;
  pseudoLogin?: string | null;
  fullName: string | null;
  role: string;
  locale?: string; // Story 11.1: i18n locale preference
  createdAt?: string; // ISO datetime string
}

// Login result type
export interface LoginResult {
  success: boolean;
  token?: string;
  user?: User;
  requires2FA?: boolean;
  sessionToken?: string;
  gracePeriodStarted?: boolean;
  graceDaysRemaining?: number | null;
  totpDisabledByAdmin?: boolean; // Story 6-6-9: admin recovery banner
}

// Transform API user to frontend User
function transformUser(apiUser: ApiUser): User {
  return {
    id: apiUser.id,
    email: apiUser.email,
    pseudoLogin: apiUser.pseudo_login ?? null,
    fullName: apiUser.full_name,
    role: apiUser.role,
    locale: apiUser.locale ?? "en",
    createdAt: apiUser.created_at,
  };
}

export const authService = {
  /**
   * Login with email or pseudo_login handle and password
   * Returns result indicating if 2FA is required or login succeeded
   */
  async login(identifier: string, password: string): Promise<LoginResult> {
    try {
      const loginData: LoginRequest = { email: identifier, password };
      const response = await api.post<LoginResponse>("/auth/login", loginData);

      // Check if 2FA is required
      if (response.data.requires_2fa && response.data.session_token) {
        return {
          success: false,
          requires2FA: true,
          sessionToken: response.data.session_token,
        };
      }

      const token = response.data.access_token;

      // Get user info after login (Story 6-6-9: includes 2FA and grace period)
      const userResponse = await api.get<ApiUser>("/auth/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const apiUser = userResponse.data;

      const user = transformUser(apiUser);

      // Apply user's locale preference (Story 11.1)
      if (apiUser.locale) {
        await i18next.changeLanguage(apiUser.locale);
      }

      return {
        success: true,
        token,
        user,
        gracePeriodStarted: apiUser.totp_grace_started ?? response.data.grace_period_started,
        graceDaysRemaining: apiUser.totp_grace_days_remaining ?? response.data.grace_days_remaining,
        totpDisabledByAdmin: apiUser.totp_disabled_by_admin ?? false,
      };
    } catch (error) {
      // Check for 403 (forbidden) which indicates inactive/unauthorized account
      const axiosError = error as { response?: { status?: number; data?: { detail?: string; message?: string } } };
      if (axiosError.response?.status === 403) {
        const detail = axiosError.response.data?.detail || axiosError.response.data?.message || "You are not able to login, please contact an administrator";
        throw new Error(detail);
      }
      // Check for 429 (rate limit) which should show the clear message from backend
      if (axiosError.response?.status === 429) {
        const detail = axiosError.response.data?.message || axiosError.response.data?.detail || "Too many login attempts. Please wait before trying again.";
        throw new Error(detail);
      }
      const message = getApiErrorMessage(error, "Login failed. Please try again.");
      throw new Error(message);
    }
  },

  /**
   * Get current user info (Story 6-6-9: includes 2FA and grace period)
   */
  async getCurrentUser(): Promise<{ user: User; apiUser: ApiUser }> {
    try {
      const response = await api.get<ApiUser>("/auth/me");
      const apiUser = response.data;
      // Apply user's locale preference (Story 11.1)
      if (apiUser.locale) {
        await i18next.changeLanguage(apiUser.locale);
      }
      return { user: transformUser(apiUser), apiUser };
    } catch (error) {
      const message = getApiErrorMessage(error, "Failed to get user information");
      throw new Error(message);
    }
  },

  /**
   * Logout (client-side token removal)
   */
  logout(): void {
    localStorage.removeItem("auth-storage");
  },

  /**
   * Request password reset (email or pseudo_login; Story 7-10)
   */
  async requestPasswordReset(identifier: string): Promise<string> {
    try {
      const response = await api.post<{ message: string }>("/auth/forgot-password", { email: identifier });
      return response.data.message;
    } catch (error) {
      const axiosError = error as { response?: { status?: number; data?: { detail?: string; message?: string } } };
      // Check for rate limit
      if (axiosError.response?.status === 429) {
        const detail = axiosError.response.data?.message || axiosError.response.data?.detail || "Too many reset requests. Please wait before requesting another.";
        throw new Error(detail);
      }
      const message = getApiErrorMessage(error, "Failed to send reset email. Please try again.");
      throw new Error(message);
    }
  },

  /**
   * Admin: check if email or pseudo handle is available (FR142)
   */
  async checkIdentifierAvailable(value: string, excludeUserId?: string): Promise<boolean> {
    const response = await api.get<{ available: boolean }>("/admin/users/check-identifier", {
      params: { value, exclude_user_id: excludeUserId },
    });
    return response.data.available;
  },

  /**
   * Validate reset token (Story 6-6-7)
   */
  async validateResetToken(token: string): Promise<{ valid: boolean; reason?: string }> {
    try {
      const response = await api.get<{ valid: boolean; reason?: string }>("/auth/reset-password/validate", {
        params: { token },
      });
      return response.data;
    } catch (error) {
      const message = getApiErrorMessage(error, "Failed to validate reset token");
      throw new Error(message);
    }
  },

  /**
   * Reset password using token (Story 6-6-7)
   */
  async resetPassword(token: string, newPassword: string, confirmPassword: string): Promise<void> {
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
    } catch (error) {
      const axiosError = error as { response?: { status?: number; data?: { detail?: string; message?: string } } };
      // Check for rate limit
      if (axiosError.response?.status === 429) {
        const detail = axiosError.response.data?.message || axiosError.response.data?.detail || "Too many reset attempts. Please wait before trying again.";
        throw new Error(detail);
      }
      const message = getApiErrorMessage(error, "Failed to reset password. Please try again.");
      throw new Error(message);
    }
  },
};
